import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { AnalyticsContext } from '../../entities/analytics-context';

export type AIChatDrawerPosition = 'right' | 'left' | 'bottom';

export interface AIChatParams {
  projectId?: string;
  taskManager?: string;
  suggestedPrompts?: string[];
  insightText?: string;
  analyticsContext?: AnalyticsContext;
  // Pin routing to one agent — required for Trello/Standalone, which don't have Jira's
  // boardId fast-path (see v3-contract.md §3-4).
  connectedAgents?: string[];
}

interface AIChatDrawerPrefs {
  pinned: boolean;
  position: AIChatDrawerPosition;
}

interface AIChatContextValue {
  isOpen: boolean;
  chatParams: AIChatParams;
  isPinned: boolean;
  position: AIChatDrawerPosition;
  openChat: (params: AIChatParams) => void;
  closeChat: () => void;
  togglePinned: () => void;
  setPosition: (position: AIChatDrawerPosition) => void;
}

const DRAWER_PREFS_KEY = 'mflow_ai_chat_drawer_prefs';

const isDrawerPosition = (value: unknown): value is AIChatDrawerPosition =>
  value === 'right' || value === 'left' || value === 'bottom';

const loadDrawerPrefs = (): AIChatDrawerPrefs => {
  try {
    const raw = localStorage.getItem(DRAWER_PREFS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<AIChatDrawerPrefs>;
      return {
        pinned: parsed.pinned === true,
        position: isDrawerPosition(parsed.position) ? parsed.position : 'right',
      };
    }
  } catch {
    // Ignore malformed prefs and fall back to defaults.
  }
  return { pinned: false, position: 'right' };
};

const AIChatContext = createContext<AIChatContextValue>({
  isOpen: false,
  chatParams: {},
  isPinned: false,
  position: 'right',
  openChat: () => {},
  closeChat: () => {},
  togglePinned: () => {},
  setPosition: () => {},
});

export function AIChatProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [chatParams, setChatParams] = useState<AIChatParams>({});
  const [drawerPrefs, setDrawerPrefs] = useState<AIChatDrawerPrefs>(loadDrawerPrefs);

  useEffect(() => {
    try {
      localStorage.setItem(DRAWER_PREFS_KEY, JSON.stringify(drawerPrefs));
    } catch {
      // Ignore storage write failures (private browsing, etc.).
    }
  }, [drawerPrefs]);

  const openChat = useCallback((params: AIChatParams) => {
    setChatParams(params);
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const togglePinned = useCallback(() => {
    setDrawerPrefs((prev) => ({ ...prev, pinned: !prev.pinned }));
  }, []);

  const setPosition = useCallback((position: AIChatDrawerPosition) => {
    setDrawerPrefs((prev) => ({ ...prev, position }));
  }, []);

  return (
    <AIChatContext.Provider
      value={{
        isOpen,
        chatParams,
        isPinned: drawerPrefs.pinned,
        position: drawerPrefs.position,
        openChat,
        closeChat,
        togglePinned,
        setPosition,
      }}
    >
      {children}
    </AIChatContext.Provider>
  );
}

export function useAIChat(): AIChatContextValue {
  return useContext(AIChatContext);
}
